/**
 * Format a Romanian name with proper capitalization and hyphenation.
 * - Only letters, spaces, and hyphens allowed
 * - User can type ONE space to separate two names (converted to hyphen)
 * - Max 2 parts (for double-barreled names)
 * - Each part capitalized (first letter upper, rest lower)
 * 
 * Example: "ioan bogdan" → "Ioan-Bogdan"
 * 
 * @param {string} value - The raw name input
 * @returns {string} - The formatted name
 */
export const formatName = (value) => {
  // Remove anything that's not a letter, space, or hyphen
  let cleaned = value.replace(/[^a-zA-ZăâîșțĂÂÎȘȚ\s-]/g, '');
  
  // Check if user just typed a space at the end (to allow continuing to type second name)
  const endsWithSpace = cleaned.endsWith(' ');
  
  // Replace multiple spaces/hyphens with single hyphen
  cleaned = cleaned.replace(/[\s-]+/g, '-');
  
  // Remove leading hyphens only (keep trailing hyphen if user is typing)
  cleaned = cleaned.replace(/^-+/, '');
  
  // Split by hyphen and take max 2 parts
  const parts = cleaned.split('-').filter(p => p.length > 0).slice(0, 2);
  
  // Capitalize each part (first letter upper, rest lower)
  const formatted = parts
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('-');
  
  // If user just typed a space and we only have one part, add hyphen at end
  // This allows the user to continue typing the second name
  if (endsWithSpace && parts.length === 1 && formatted.length > 0) {
    return formatted + '-';
  }
  
  return formatted;
};

/**
 * Validate Romanian mobile phone number.
 * Rules:
 * - Exactly 10 digits
 * - Only digits allowed
 * - Must start with "0"
 * 
 * @param {string} phone - The phone number to validate
 * @returns {{ valid: boolean, error: string | null }} - Validation result
 */
export const validatePhone = (phone) => {
  if (!phone || phone.trim() === '') {
    return { valid: true, error: null }; // Phone is optional
  }
  
  // Remove any spaces or dashes that might have been entered
  const cleaned = phone.replace(/[\s-]/g, '');
  
  // Check if only digits
  if (!/^\d+$/.test(cleaned)) {
    return { valid: false, error: 'Numărul de telefon poate conține doar cifre' };
  }
  
  // Check if starts with 0
  if (!cleaned.startsWith('0')) {
    return { valid: false, error: 'Numărul de telefon trebuie să înceapă cu 0' };
  }
  
  // Check length
  if (cleaned.length !== 10) {
    return { valid: false, error: 'Numărul de telefon trebuie să aibă exact 10 cifre' };
  }
  
  return { valid: true, error: null };
};

/**
 * Format phone number input - only allow digits, max 10.
 * 
 * @param {string} value - The raw phone input
 * @returns {string} - The formatted phone (digits only, max 10)
 */
export const formatPhone = (value) => {
  // Remove anything that's not a digit
  const digits = value.replace(/\D/g, '');
  // Limit to 10 digits
  return digits.slice(0, 10);
};
